import { BadgeCardRow, LazyLoader } from '@bp/ui-components';
import dayjs from 'dayjs';
import i18next from 'i18next';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetHolidays } from '../../../hooks/useGetHolidays';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { useTeachingBlockDataCards } from '../../../hooks/useTeachingBlockDataCards';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { availabilityStoreHandler } from '../../../pages/Timetable/Plan/TeachingBlockVersion/TeachingBlockVersion';
import { ClassDivisionGroupFormat } from '../../../stores/DayAvailabilityStore';
import { StoreHandler } from '../../../stores/StoreHandler';
import { TeachingBlockStore } from '../../../stores/TeachingBlockVersion/TeachingBlockStore';
import {
  TeachingBlockResourceType,
  useLessonsForTeachingBlockVersionQuery,
  useSubjectContainerPlaceholderLessonsQuery,
  useTeachingBlockClassTripCardsQuery,
} from '../../../types/planung-graphql-client-defs';
import { createIntArray } from '../../../utils/arrayFunc';
import { getDaysDifference } from '../../../utils/dateCalculations';
import { isNotEmpty } from '../../../utils/typeguards';
import { useTeachingBlockVersionData } from '../../TeachingBlockVersion/hooks/useTeachingBlockVersionData';
import { boardCardsSignature, buildBoardCards } from '../buildBoardCards';
import { collectForeignClassTrips } from '../foreignClassTrips';
import { buildForeignTripIndex } from '../foreignTripIndex';
import { TeachingBlockBoardVersion, TeachingBlockCardType } from '../types';
import { useTeachingBlockConflicts } from './TeachingBlockConflictContext';

const HOLDER_TYPENAME = {
  [TeachingBlockResourceType.Class]: 'Class',
  [TeachingBlockResourceType.Teacher]: 'Person',
  [TeachingBlockResourceType.Room]: 'Room',
  [TeachingBlockResourceType.Subject]: 'Subject',
} as const;

const teachingBlockStoreHandler = new StoreHandler<TeachingBlockStore>();
/** Je Version die Kennung der Karten, mit denen ihr Store gebaut wurde (s. `boardCardsSignature`). */
const storeCardsSignatures = new Map<string, string>();
const TeachingBlockStoreContext = createContext<TeachingBlockStore>({} as TeachingBlockStore);

export const TeachingBlockProvider: FC<PropsWithChildren> = ({ children }) => {
  const { teachingBlockVersionUuid } = useParams();
  const permissions = usePermissionChecker();
  const userConfig = useUserConfigContext();
  const { holidays, schoolYear } = useGetHolidays();

  const lessonContext = useMemorizedCacheTag('LESSONS');

  // Lesemodell der Version: Kopfdaten, Ressourcen, Karten und Ausschlüsse in einer Anfrage.
  const { versionData, resourceUuids } = useTeachingBlockVersionData(teachingBlockVersionUuid);
  const { calculateConflicts } = useTeachingBlockConflicts();
  const availabilityStore = availabilityStoreHandler.get(versionData?.meta.versionUuid ?? '');

  // Dieselbe Abfrage wie in der Zuordnungs-Tabelle der Version; urql teilt sich das Ergebnis.
  const [{ data: lessonsQueryData }] = useLessonsForTeachingBlockVersionQuery({
    variables: { versionUuid: teachingBlockVersionUuid ?? '' },
    pause: !teachingBlockVersionUuid || teachingBlockVersionUuid === '',
    context: lessonContext,
  });

  const { subjectData, classesData, allPersons, groupsData, roomsData } = useLoadBasicData({
    pause: !versionData,
  });

  const subjectContainerUuid = versionData?.meta.subjectContainerUuid;

  const [{ data: placeholderLessonsData }] = useSubjectContainerPlaceholderLessonsQuery({
    variables: {
      subjectContainerUuid: subjectContainerUuid ?? '',
      schoolYearUuid: schoolYear?.uuid ?? '',
    },
    pause: !subjectContainerUuid || !schoolYear?.uuid,
  });

  // Klassenfahrten aus fremden aktiven Plänen: nur als Dependency, nicht als Guard-Bedingung
  // des Store-useMemo. Die Reihenfolge ist bereits über Suspense garantiert (urqlClient läuft
  // mit suspense: true), daher rendert die Komponente nicht am Query-Hook vorbei, bevor er
  // aufgelöst ist. Ein Guard wäre hier zusätzlich gefährlich: mapExchange (urqlClient.ts)
  // setzt data bei jedem GraphQL-Fehler auf undefined – ein rein dekoratives Overlay würde so
  // zur blockierenden Voraussetzung für das gesamte Board.
  const [{ data: classTripData }] = useTeachingBlockClassTripCardsQuery({
    variables: { schoolYearUuid: schoolYear?.uuid ?? '' },
    pause: !schoolYear?.uuid,
  });

  const placeholderLessonGroups = useMemo((): Set<string>[] => {
    return (
      placeholderLessonsData?.lessonsForSubjectContainer.map(
        (lesson) => new Set(lesson.lessonClasses.map((lessonClass) => lessonClass.class.uuid)),
      ) ?? []
    );
  }, [placeholderLessonsData]);

  const {
    classes: versionClasses,
    rooms: versionRooms,
    teachers: versionTeachers,
    subjects: versionSubjects,
  } = resourceUuids;

  const teachingBlockDataCards = useTeachingBlockDataCards(teachingBlockVersionUuid ?? '');

  const rooms = useMemo(() => {
    return (
      roomsData?.rooms.map((r) => ({
        ...r,
        building: { ...r.building, uuid: r.building?.uuid ?? '', name: r.building?.name ?? '' },
      })) ?? []
    );
  }, [roomsData?.rooms]);

  const cards: TeachingBlockCardType[] | undefined = useMemo(() => {
    const versionCards = versionData?.cards;
    const lessons = lessonsQueryData?.lessonsForTeachingBlockVersion;
    if (!versionCards || !lessons) return undefined;

    return buildBoardCards({
      cards: versionCards,
      lessons,
      gridCards: teachingBlockDataCards,
      subjects: subjectData?.subjects ?? [],
      classes: classesData?.classes ?? [],
      people: allPersons?.people ?? [],
      groups: groupsData?.groups ?? [],
    });
  }, [
    classesData?.classes,
    versionData?.cards,
    groupsData?.groups,
    lessonsQueryData?.lessonsForTeachingBlockVersion,
    subjectData?.subjects,
    allPersons?.people,
    teachingBlockDataCards,
  ]);

  // Die Version in der Form, die die Stores erwarten: Ressourcen der Version als Zeilen des Boards.
  const currentVersion: TeachingBlockBoardVersion | undefined = useMemo(() => {
    if (!versionData || !classesData?.classes || !allPersons?.people || !roomsData?.rooms) return undefined;
    const { meta } = versionData;
    const classUuids = new Set(versionClasses);
    const teacherUuids = new Set(versionTeachers);
    const roomUuids = new Set(versionRooms);
    return {
      uuid: meta.versionUuid,
      version: meta.version,
      description: meta.description,
      active: meta.active,
      editable: meta.editable,
      isSnapshot: meta.isSnapshot,
      classesConnection: {
        edges: classesData.classes.filter((c) => classUuids.has(c.uuid)).map((node) => ({ node })),
      },
      teachersConnection: {
        edges: allPersons.people
          .filter((p) => teacherUuids.has(p.uuid))
          .sort((a, b) => a.lastName.localeCompare(b.lastName))
          .map((node) => ({ node })),
      },
      roomsConnection: {
        edges: roomsData.rooms
          .filter((r) => roomUuids.has(r.uuid))
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((node) => ({ node })),
      },
    };
  }, [
    versionData,
    classesData?.classes,
    allPersons?.people,
    roomsData?.rooms,
    versionClasses,
    versionTeachers,
    versionRooms,
  ]);

  // Woraus die Karten bestehen (ohne Position): ändert sich das außerhalb des Boards, etwa in der
  // Zuordnungs-Tabelle, muss der gecachte Store neu gebaut werden – sonst arbeitet das Board mit
  // Karten, die es nicht mehr gibt, und kennt die neuen nicht.
  const cardsSignature = useMemo(() => {
    const versionCards = versionData?.cards;
    const lessons = lessonsQueryData?.lessonsForTeachingBlockVersion;
    return versionCards && lessons ? boardCardsSignature(versionCards, lessons) : undefined;
  }, [versionData?.cards, lessonsQueryData?.lessonsForTeachingBlockVersion]);

  const teachingBlockStore = useMemo(() => {
    if (
      teachingBlockVersionUuid &&
      holidays &&
      schoolYear &&
      cards &&
      currentVersion &&
      roomsData?.rooms &&
      classesData?.classes &&
      allPersons?.people &&
      subjectData?.subjects &&
      cardsSignature !== undefined
    ) {
      const builtWith = storeCardsSignatures.get(teachingBlockVersionUuid);
      if (builtWith !== undefined && builtWith !== cardsSignature) {
        teachingBlockStoreHandler.unregister(teachingBlockVersionUuid);
      }

      return teachingBlockStoreHandler.register(teachingBlockVersionUuid, () => {
        storeCardsSignatures.set(teachingBlockVersionUuid, cardsSignature);
        // Ausserhalb der `if (availabilityStore)`-Klammer gebaut, damit sowohl die Übertragung
        // in den availabilityStore weiter unten als auch der Store-Aufbau (Index) sie sehen.
        const foreignTrips = collectForeignClassTrips({
          data: classTripData,
          currentVersionUuid: currentVersion.uuid,
          classTripLabel: i18next.t('lesson.basics.isClassTrip'),
        });

        if (availabilityStore) {
          versionData?.exclusions.forEach((exclusion) => {
            availabilityStore.setExclusion({
              holder: { __typename: HOLDER_TYPENAME[exclusion.holderType], uuid: exclusion.holderUuid },
              from: new Date(exclusion.from),
              to: new Date(exclusion.to),
              priority: exclusion.priority,
            });
          });

          foreignTrips.forEach((trip) => {
            availabilityStore.setClassTrip({
              from: trip.from,
              to: trip.to,
              holders: trip.holders,
            });
          });

          cards.forEach((card) => {
            if (card.startDate !== null && card.endDate !== null) {
              const classDivisionGroups: ClassDivisionGroupFormat[] = [];
              card.lessonClasses.forEach((lc) => {
                lc?.groups.forEach((g) => {
                  if (lc.usedDivision) {
                    classDivisionGroups.push(
                      `${lc.class.uuid}:${lc.usedDivision?.uuid ? lc.usedDivision.uuid : ''}:${g.uuid}`,
                    );
                  }
                });
              });

              // availabilityStore.setCardsResourcesPlaced(
              //   [
              //     {
              //       uuid: card.uuid,
              //       teachers: card.teachers.map((t) => t.uuid),
              //       classes: card.classes.map((c) => c.uuid),
              //       classDivisionGroups: classDivisionGroups,
              //       rooms: card.rooms.map((r) => r.uuid),
              //       subject: card.subject?.uuid ?? '',
              //     },
              //   ],
              //   {
              //     start: card.startDate,
              //     end: card.endDate,
              //   },
              // );
            }
          });
        }

        const store = new TeachingBlockStore({
          holidays,
          schoolYear,
          rooms,
          initialCards: cards,
          foreignTripIndex: buildForeignTripIndex(foreignTrips),
          currentVersion,
          calculateConflicts,
          placeholderLessonGroups,
          userConfig,
          readonly: !permissions?.canWriteTimetable()
            ? true
            : currentVersion.isSnapshot
              ? true
              : isNotEmpty(currentVersion.editable)
                ? !currentVersion.editable
                : false,
          temporarilyEditable: (currentVersion.active && currentVersion.editable) ?? false,
        });

        cards.forEach((c) => store.mapRoomsForCard(c));

        store.loadData({
          classes: classesData?.classes.filter((c) => versionClasses.includes(c.uuid)),
          rooms: roomsData?.rooms
            .filter((c) => versionRooms.includes(c.uuid))
            .sort((a, b) => a.name.localeCompare(b.name)),
          teachers: allPersons?.people
            .filter((c) => versionTeachers.includes(c.uuid))
            .sort((a, b) => a.lastName.localeCompare(b.lastName)),
          subjects: subjectData?.subjects
            .filter((c) => versionSubjects.includes(c.uuid))
            .sort((a, b) => a.name.localeCompare(b.name)),
        });
        return store;
      });
    }

    return null;
  }, [
    versionData?.exclusions,
    availabilityStore,
    calculateConflicts,
    cards,
    cardsSignature,
    classesData?.classes,
    classTripData,
    currentVersion,
    holidays,
    rooms,
    roomsData?.rooms,
    schoolYear,
    subjectData?.subjects,
    allPersons?.people,
    teachingBlockVersionUuid,
    versionClasses,
    versionRooms,
    versionSubjects,
    versionTeachers,
    permissions,
    placeholderLessonGroups,
    userConfig,
  ]);

  useEffect(() => {
    if (teachingBlockStore) {
      teachingBlockStore.setPlaceholderLessonGroups(placeholderLessonGroups);
    }
  }, [placeholderLessonGroups, teachingBlockStore]);

  // Ressourcen-Maps des (in StoreHandler gecachten) Stores an die Version koppeln:
  // Nach Import/Entfernen (connect/disconnect) invalidiert die Mutation die
  // TeachingBlockVersion-Query, `versionTeachers/…` aktualisieren sich – ohne diesen
  // Sync bliebe der einmal geladene Store stale und die Tabellen zeigten die Änderung
  // erst nach einem Reload. `loadData` setzt ausschließlich die vier Ressourcen-Maps
  // (keine Karten), ist also gefahrlos wiederholbar.
  useEffect(() => {
    if (teachingBlockStore && allPersons?.people && subjectData?.subjects && roomsData?.rooms && classesData?.classes) {
      teachingBlockStore.loadData({
        classes: classesData.classes.filter((c) => versionClasses.includes(c.uuid)),
        rooms: roomsData.rooms
          .filter((r) => versionRooms.includes(r.uuid))
          .sort((a, b) => a.name.localeCompare(b.name)),
        teachers: allPersons.people
          .filter((p) => versionTeachers.includes(p.uuid))
          .sort((a, b) => a.lastName.localeCompare(b.lastName)),
        subjects: subjectData.subjects
          .filter((s) => versionSubjects.includes(s.uuid))
          .sort((a, b) => a.name.localeCompare(b.name)),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    teachingBlockStore,
    versionClasses,
    versionRooms,
    versionTeachers,
    versionSubjects,
    allPersons?.people,
    subjectData?.subjects,
    roomsData?.rooms,
    classesData?.classes,
  ]);

  return (
    <>
      {teachingBlockStore ? (
        <TeachingBlockStoreContext.Provider value={teachingBlockStore}>{children}</TeachingBlockStoreContext.Provider>
      ) : (
        <LazyLoader transparent forceHeight='60vh' />
      )}
    </>
  );
};

export const useTeachingBlockStore = () => useContext(TeachingBlockStoreContext);
